import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { loadRealms, attachedTo, holdingRealm, kindOne, profileOf } from '../nucleus/RealmsModel';
import { yamlOf } from '../nucleus/RealmsEditor';

// REALMS AND ACCESS, ONE READING. Realms are the shapes of access you own (what
// may be seen); a grant is one connection's exposure profile (who sees, through
// which realms, as which identity, until when). Both sections read the same
// space's realms and grants, here, and change them through the same calls.
//
// GROUP vs PERSONAL, every call (the space you are in, never the other):
//   realms                     <realms> = /groups/<g>/privacy-realms | /privacy-realms
//   grants                     <grants> = /groups/<g>/exposure-profiles | /exposure-profiles
//   pause / resume / disconnect / appear-as     <grants>/<id>…
//   pause-all / resume-all     <grants>/pause-all | resume-all
//   attach a realm             personal: POST /exposure-profiles/<id>/realms
//                              group:    PUT <grants>/<id> {privacy_realm_ids}  (no group attach route)
//   detach / take out          personal: DELETE /exposure-profiles/<id>/realms/<r>
//                                        (from the realm: DELETE /privacy-realms/<r>/attachments/<id>)
//                              group:    PUT <grants>/<id> {privacy_realm_ids}
//   fork                       personal: POST /privacy-realms/<id>/fork
//                              group:    POST <realms> with the ceiling (no group fork route; the
//                                        group create takes no identity, so the copy has none)
//   history                    GET /exposure-profiles/<id>/history — no group route exists; this
//                              one serves a group's grants to its members
//   import YAML                POST /privacy-realms/parse-yaml — stateless, the same in a group
//   preview                    a grant: GET /exposure-profiles/<id>/preview (serves a group's too)
//                              a realm: GET <realms>/<id>/preview

export const bases = (groupId) => ({
    realms: groupId ? `/groups/${groupId}/privacy-realms` : '/privacy-realms',
    grants: groupId ? `/groups/${groupId}/exposure-profiles` : '/exposure-profiles',
});

const faceOf = (n) => ({ id: String(n.id), name: n.value_json?.display_name || n.value_json?.name || n.title || 'Identity', node: n });

// Whom a just-for-one realm is for: the newest exposure profile to its recipient.
export function recipientOf(realm, profiles) {
    if (!realm?.recipientId) return null;
    const theirs = profiles.filter((p) => String(p.entityId) === realm.recipientId && (!realm.recipientType || p.entityType === realm.recipientType));
    return theirs.find((p) => p.live) || theirs[0] || null;
}
export const recipientName = (realm, profiles) => recipientOf(realm, profiles)?.label
    || (realm.name.startsWith('→ ') ? realm.name.slice(2) : 'one recipient');

// Names, for a sentence: "Mira (person), Notebook (app) and 3 more".
export function namesOf(list) {
    const ns = list.map((p) => `${p.label} (${kindOne(p.kind).toLowerCase()})`);
    return ns.length > 6 ? `${ns.slice(0, 6).join(', ')} and ${ns.length - 6} more` : ns.join(', ');
}

// The space's realms, grants and identities, read together.
export function useRealmsData(groupId, group) {
    const { user } = useAuth() || {};
    const [data, setData] = useState(null);       // null while loading · { realms, profiles, schemaNames } · { error }
    const [faces, setFaces] = useState([]);

    const loadFaces = useCallback(() => {
        (groupId ? api.getGroupNodes(groupId, { schema_name: 'profile', limit: 50 }) : api.getNodes({ schema_id: 'profile', limit: 50 }))
            .then((r) => setFaces((Array.isArray(r) ? r : r?.nodes || []).filter((n) => n?.id).map(faceOf)))
            .catch(() => setFaces([]));
    }, [groupId]);

    // only the newest read lands: a slow answer for the space you just left
    // must never paint over the one you are in
    const latest = useRef(0);
    const dataRef = useRef(data);         // what a grants-only reload maps against
    dataRef.current = data;
    const load = useCallback(async () => {
        const ticket = ++latest.current;
        try {
            // The kinds by name are read INSIDE loadRealms, alongside the realms and the
            // grants rather than before them (in a group, the group's kinds). In a group,
            // who is on which realm is read at the same time: every member has one.
            const [d, roster] = await Promise.all([
                loadRealms(groupId),
                groupId ? api.request(`/groups/${groupId}/members`).then((r) => r?.members || []).catch(() => []) : Promise.resolve([]),
            ]);
            // Admins sit on Full Access: their grant says so, and one that predates it
            // is still read there.
            const fullAccess = d.realms.find((r) => r.builtin === 'full_access');
            const members = roster.filter(Boolean).map((m) => ({
                id: String(m.user_id || m.user?.id || m.id),
                name: m.user?.username || m.username || 'Someone',
                role: m.role === 'admin' ? 'admin' : 'member',
                realmId: m.privacy_realm_id ? String(m.privacy_realm_id) : m.role === 'admin' && fullAccess ? fullAccess.id : null,
            })).filter((m) => m.realmId)
                // admins first, then by name
                .sort((a, b) => (a.role === b.role ? 0 : a.role === 'admin' ? -1 : 1) || a.name.localeCompare(b.name));
            if (ticket === latest.current) setData({ ...d, ...splitGrants(d.profiles), members });
        } catch (e) {
            if (ticket === latest.current) setData((d) => (d && !d.error ? d : { error: e?.message || 'Could not read your realms.' }));
        }
    }, [groupId]);

    // AFTER AN ACT ON A GRANT, ONLY THE GRANTS. Pausing, resuming, revoking, choosing a
    // face, adding or removing a realm: each changes grants and nothing else on the page,
    // so the kinds, the realms and the roster stay as read and only the grants list is
    // asked again — mapped with the names and realms already held. A change to a realm
    // itself (made, edited, duplicated, deleted) is still `reload`, which reads it all.
    const loadGrants = useCallback(async () => {
        const ticket = ++latest.current;
        const held = dataRef.current;
        if (!held || held.error) return load();
        try {
            const r = await api.request(bases(groupId).grants);
            const byId = Object.fromEntries(held.realms.map((x) => [x.id, x]));
            const all = (r?.exposure_profiles || r?.profiles || []).map((j) => profileOf(j, held.schemaNames, byId)).filter(Boolean);
            if (ticket === latest.current) setData((d) => (d && !d.error ? { ...d, ...splitGrants(all) } : d));
        } catch {
            if (ticket === latest.current) load();
        }
    }, [groupId, load]);

    useEffect(() => { setData(null); load(); }, [load]);
    useEffect(() => { loadFaces(); }, [loadFaces]);

    return useMemo(() => {
        const realms = data?.realms || [];
        const profiles = data?.profiles || [];
        const defaultFaceId = (groupId ? group?.default_profile_node_id : user?.default_profile_node_id) || null;
        const faceName = (id) => (id ? faces.find((f) => f.id === String(id))?.name || null : null);
        return {
            loaded: !!data && !data.error,
            error: data?.error || null,
            realms,
            profiles,
            memberGrants: data?.memberGrants || [],
            live: profiles.filter((p) => p.live),
            schemaNames: data?.schemaNames || {},
            schemaKeys: data?.schemaKeys || {},
            faces,
            defaultFaceId,
            faceName,
            // who a realm reaches: every live grant ATTACHED to it, not the primary pointer
            inside: (realm) => attachedTo(profiles, realm.id),
            // what keeps it from being deleted: lapsed grants too (they can be extended)
            holding: (realm) => holdingRealm(profiles, realm.id),
            // in a group, who is on it: members by their realm, admins on Full Access
            givenTo: (realm) => (data?.members || []).filter((m) => m.realmId === String(realm.id)),
            // how a realm presents you: its own identity, else the space's default
            faceWord: (realm) => faceName(realm.profileNodeId)
                || (realm.profileNodeId ? 'an identity' : faceName(defaultFaceId) ? `${faceName(defaultFaceId)} (default)` : 'your default identity'),
            reload: load,
            reloadGrants: loadGrants,
            reloadFaces: loadFaces,
        };
    }, [data, faces, groupId, group, user, load, loadGrants, loadFaces]);
}

// A group's grant to one of its own members is how that member holds the realm an
// admin gave them (People's to show), not a contract: it stands apart from every
// contracts view.
function splitGrants(all) {
    return {
        memberGrants: all.filter((p) => p.entityType === 'group_member'),
        profiles: all.filter((p) => p.entityType !== 'group_member'),
    };
}

// Every call that changes a realm or a grant, in this space.
export function realmsApi(groupId) {
    const b = bases(groupId);
    const req = (path, method, body) => api.request(path, { method, ...(body ? { body } : {}) });
    const onlyRealm = (realmName, p) => new Error(`“${realmName}” is the only realm ${p.label} is shared from. Revoke its contract instead.`);
    return {
        pause: (p) => req(`${b.grants}/${p.id}/pause`, 'POST'),
        resume: (p) => req(`${b.grants}/${p.id}/resume`, 'POST'),
        disconnect: (p) => req(`${b.grants}/${p.id}`, 'DELETE'),
        // a friend request's offer: cancel the request it sits in (the offer goes
        // with it); without that request in hand, revoking the offer cancels it too
        cancelRequest: async (p) => {
            const r = await api.getOutgoingFollows({ relationship_type: 'friend_request', limit: 100 }).catch(() => null);
            const ask = (r?.follows || []).find((f) => String(f.follower_ep_id || f.follower_ep?.id) === String(p.id)
                && (f.status === 'pending' || f.status === 'declined'));
            return ask ? api.revokeFollow(ask.id) : req(`${b.grants}/${p.id}`, 'DELETE');
        },
        // an access token: made on one realm of this space, revoked as a token so the
        // token and its contract end together (routes/access_tokens.py)
        mintToken: (body) => req('/access-tokens', 'POST', groupId ? { ...body, group_id: groupId } : body),
        revokeToken: (p) => (p.token?.id ? req(`/access-tokens/${p.token.id}`, 'DELETE') : req(`${b.grants}/${p.id}`, 'DELETE')),
        pauseAll: () => req(`${b.grants}/pause-all`, 'POST'),
        resumeAll: () => req(`${b.grants}/resume-all`, 'POST'),
        setFace: (p, body) => req(`${b.grants}/${p.id}`, 'PUT', body),
        attach: (p, realmId) => (groupId
            ? req(`${b.grants}/${p.id}`, 'PUT', { privacy_realm_ids: [...p.realmIds, String(realmId)] })
            : req(`/exposure-profiles/${p.id}/realms`, 'POST', { realm_id: realmId })),
        // the last realm is never detached: it would fall back to the primary
        // pointer, not revoke anything; revoking is the act for that
        detach: (p, realm) => {
            const rest = p.realmIds.filter((x) => x !== String(realm.id));
            if (!rest.length) return Promise.reject(onlyRealm(realm.name, p));
            return groupId
                ? req(`${b.grants}/${p.id}`, 'PUT', { privacy_realm_ids: rest })
                : req(`/exposure-profiles/${p.id}/realms/${realm.id}`, 'DELETE');
        },
        takeOut: (realm, p) => {
            const rest = p.realmIds.filter((x) => x !== String(realm.id));
            if (!rest.length) return Promise.reject(onlyRealm(realm.name, p));
            return groupId
                ? req(`${b.grants}/${p.id}`, 'PUT', { privacy_realm_ids: rest })
                : req(`/privacy-realms/${realm.id}/attachments/${p.id}`, 'DELETE');
        },
        deleteRealm: (realm) => req(`${b.realms}/${realm.id}`, 'DELETE'),
        fork: (realm, name) => (groupId
            ? req(b.realms, 'POST', {
                name, description: `A copy of ${realm.name}`, ceiling_yaml: yamlOf(realm.ceilingBlob),
                ...(realm.excludedNodeIds ? { excluded_node_ids: realm.excludedNodeIds } : {}),
            })
            : req(`/privacy-realms/${realm.id}/fork`, 'POST', { name })),
        history: (p) => api.request(`/exposure-profiles/${p.id}/history`),
        newFace: async (name) => {
            const body = { title: name, value_json: { display_name: name }, schema_name: 'profile' };
            const res = groupId ? await api.createGroupNode(groupId, body) : await api.request('/nodes', { method: 'POST', body });
            return res?.node || res;
        },
        // what a grant can see right now, and the most a realm could let anything see
        // (authz/preview.py): a page of the nodes, searchable by title, masked
        preview: (p, q) => api.request(`/exposure-profiles/${p.id}/preview${q ? `?q=${encodeURIComponent(q)}` : ''}`),
        realmPreview: (realm, q) => api.request(`${b.realms}/${realm.id}/preview${q ? `?q=${encodeURIComponent(q)}` : ''}`),
        // the titles of the specific nodes a blob names, { id: title }, in ONE read of
        // the space's nodes by id rather than one read per node. A node it cannot read
        // is simply absent, as a failed single read was.
        nodeTitles: (ids) => (ids.length
            ? api.request(`${groupId ? `/groups/${groupId}/nodes` : '/nodes'}?ids=${ids.map(encodeURIComponent).join(',')}&limit=${ids.length}`)
                .then((r) => Object.fromEntries((Array.isArray(r) ? r : r?.nodes || []).filter((n) => n?.id)
                    .map((n) => [String(n.id), n.value_json?.title || n.title || 'untitled'])))
            : Promise.resolve({})),
    };
}
